import { useEffect, useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/context/ShopContext";

const detailText = (err) =>
  typeof err?.detail === "string" ? err.detail : "Something went wrong. Please try again.";

const Field = ({ label, children }) => (
  <div>
    <label className="overline-label block mb-2">{label}</label>
    {children}
  </div>
);

export const AdminSettings = () => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch(`${API}/admin/settings`, { credentials: "include" })
      .then((r) => r.json())
      .then(setS)
      .catch(() => toast.error("Could not load settings"));
  }, []);

  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });
  const setNum = (k) => (e) => setS({ ...s, [k]: e.target.value === "" ? 0 : parseInt(e.target.value, 10) || 0 });

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        store_name: s.store_name, store_email: s.store_email, instagram: s.instagram,
        whatsapp: s.whatsapp, phone: s.phone,
        shipping_charge: s.shipping_charge, free_shipping_threshold: s.free_shipping_threshold,
        min_order_value: s.min_order_value, delivery_info: s.delivery_info,
        announcement_enabled: s.announcement_enabled, announcement_text: s.announcement_text,
        payment_mode: s.payment_mode,
      };
      const r = await fetch(`${API}/admin/settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok) throw data;
      setS(data);
      toast.success("Settings saved — live on the website now");
    } catch (err) {
      toast.error(detailText(err));
    } finally {
      setBusy(false);
    }
  };

  if (!s) {
    return <div className="py-24 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-taupe" /></div>;
  }

  return (
    <div className="mt-10 max-w-3xl" data-testid="admin-settings">
      <section className="bg-white border border-beige p-8">
        <h2 className="font-serif text-2xl">Store & Contact</h2>
        <p className="text-[11px] text-taupe mt-1">Shown on the website footer and contact page.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-6">
          <Field label="Store Name"><input className="input-lux" data-testid="settings-store-name" value={s.store_name} onChange={set("store_name")} /></Field>
          <Field label="Business Email"><input className="input-lux" data-testid="settings-store-email" value={s.store_email} onChange={set("store_email")} /></Field>
          <Field label="Instagram Handle (without @)"><input className="input-lux" data-testid="settings-instagram" value={s.instagram} onChange={set("instagram")} /></Field>
          <Field label="WhatsApp Number (with country code)"><input className="input-lux" data-testid="settings-whatsapp" placeholder="e.g. 919876543210" value={s.whatsapp} onChange={set("whatsapp")} /></Field>
          <Field label="Contact Phone (optional)"><input className="input-lux" data-testid="settings-phone" value={s.phone} onChange={set("phone")} /></Field>
          <Field label="Currency"><input className="input-lux opacity-60" value={s.currency} disabled readOnly /></Field>
        </div>
      </section>

      <section className="bg-white border border-beige p-8 mt-6">
        <h2 className="font-serif text-2xl">Shipping & Orders</h2>
        <p className="text-[11px] text-taupe mt-1">Applied instantly to the cart, checkout and new orders.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-6">
          <Field label="Shipping Charge (₹)"><input type="number" min="0" className="input-lux" data-testid="settings-shipping-charge" value={s.shipping_charge} onChange={setNum("shipping_charge")} /></Field>
          <Field label="Free Shipping Above (₹)"><input type="number" min="0" className="input-lux" data-testid="settings-free-threshold" value={s.free_shipping_threshold} onChange={setNum("free_shipping_threshold")} /></Field>
          <Field label="Minimum Order (₹, 0 = none)"><input type="number" min="0" className="input-lux" data-testid="settings-min-order" value={s.min_order_value} onChange={setNum("min_order_value")} /></Field>
          <Field label="Shipping Partners (comma-separated)">
            <input
              className="input-lux"
              data-testid="settings-shipping-partners"
              placeholder="e.g. Delhivery, Blue Dart, DTDC, India Post"
              value={Array.isArray(s.shipping_partners) ? s.shipping_partners.join(", ") : (s.shipping_partners || "")}
              onChange={(e) => setS({ ...s, shipping_partners: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })}
            />
          </Field>
        </div>
        <div className="mt-5">
          <Field label="Delivery Information (shown on product pages)">
            <textarea rows={3} className="input-lux resize-none" data-testid="settings-delivery-info" value={s.delivery_info} onChange={set("delivery_info")} />
          </Field>
        </div>
      </section>

      <section className="bg-white border border-beige p-8 mt-6">
        <h2 className="font-serif text-2xl">Homepage Announcement</h2>
        <p className="text-[11px] text-taupe mt-1">A slim banner across the top of every page — perfect for offers or Eid greetings. Featured products are managed from the Products tab (star toggle in the editor).</p>
        <label className="flex items-center gap-3 mt-6 cursor-pointer">
          <input
            type="checkbox"
            data-testid="settings-announcement-enabled"
            checked={!!s.announcement_enabled}
            onChange={(e) => setS({ ...s, announcement_enabled: e.target.checked })}
            className="w-4 h-4 accent-[#2B211C]"
          />
          <span className="text-sm">Show announcement bar</span>
        </label>
        <input
          className="input-lux mt-4"
          data-testid="settings-announcement-text"
          placeholder="e.g. Eid Collection — complimentary shipping this week"
          value={s.announcement_text}
          onChange={set("announcement_text")}
        />
      </section>

      <section className="bg-white border border-beige p-8 mt-6">
        <div className="flex items-center gap-3">
          <h2 className="font-serif text-2xl">Payments</h2>
          <span
            className={`text-[10px] uppercase tracking-[0.2em] border px-2.5 py-1 ${
              s.payment_mode === "live" ? "border-espresso text-espresso" : "border-taupe/40 text-taupe"
            }`}
            data-testid="payment-mode-badge"
          >
            {s.payment_mode === "live" ? "Live — PayU" : "Demo Mode"}
          </span>
        </div>
        <p className="text-[11px] text-taupe mt-2 leading-relaxed">
          PayU keys are stored securely on the server{s.payu_configured ? " — configured ✓" : " — not configured yet"}.
          In Live mode customers pay via UPI, cards, netbanking and wallets on PayU's secure page.
          In Demo mode orders are recorded without charging money.
        </p>
        <Field label="Checkout Mode">
          <select
            className="input-lux mt-4"
            data-testid="settings-payment-mode"
            value={s.payment_mode}
            onChange={set("payment_mode")}
          >
            <option value="demo">Demo — no real payments</option>
            <option value="live" disabled={!s.payu_configured}>Live — real payments via PayU</option>
          </select>
        </Field>
      </section>

      <button onClick={save} disabled={busy} data-testid="settings-save" className="btn-primary w-full mt-8">
        {busy ? "Saving…" : "Save All Settings"}
      </button>
    </div>
  );
};
